// Record that a LOCKED run was settled outside KaziPeople — bank file, transfer,
// cash — and move it to `paid`.
//
// WHY THIS EXISTS. Payslips are visible in My Pay only for a run in `paid`, and
// the only thing that used to write `paid` was an in-platform disbursement
// completing. A tenant that downloads the bank file and uploads it at their bank
// therefore never reached `paid`, and their employees could never see a payslip —
// even though locking had already generated every one of them.
//
// SO THE COPY HERE SAYS WHAT REALLY HAPPENS: payslips become visible to everyone
// in the run. That is the effect people actually feel, and a dialog that only
// said "mark as paid" would hide it behind a state name.
//
// It is an ASSERTION BY A HUMAN, not an inference — nothing derives "probably
// paid" from age or state — so it asks who paid by what route, and says plainly
// that it cannot be undone from here.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { Textarea } from '@/features/shared/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/features/shared/components/ui/dialog';
import { RUN_EXTERNAL_PAYMENT_METHODS } from '../types';
import type { DisbursementBatchStatus, RunExternalPaymentMethod } from '../types';

interface MarkPaidDialogProps {
  open: boolean;
  busy: boolean;
  /** How many employees get a visible payslip out of this — the concrete effect. */
  /** null = the run's Employees read failed: the copy names no number. */
  employeeCount: number | null;
  /** Status of any KaziPeople disbursement batch on this run, for the in-flight
   *  warning. Undefined when no batch was ever prepared (the common case). */
  batchStatus?: DisbursementBatchStatus;
  onClose: () => void;
  onConfirm: (body: {
    method: RunExternalPaymentMethod;
    reference: string;
    note: string;
  }) => void;
}

export const MarkPaidDialog: React.FC<MarkPaidDialogProps> = ({
  open,
  busy,
  employeeCount,
  batchStatus,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation('payroll');
  const [method, setMethod] = useState<RunExternalPaymentMethod>('bank_file');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');

  // Reset each time it opens, so a previous abandoned attempt never becomes part
  // of the record of a different payment.
  useEffect(() => {
    if (open) {
      setMethod('bank_file');
      setReference('');
      setNote('');
    }
  }, [open]);

  // Mirrors payrollrun.MarkPaidRequest.Validate: only "Other" must be explained,
  // because requiring a reference for every method is how the field that exists
  // to make this auditable fills up with "n/a".
  const needsExplanation = method === 'other' && !reference.trim() && !note.trim();

  // A batch that is already with the provider is money possibly in flight. This
  // does NOT block: the case this feature was built for is a batch that FAILED
  // (Bavic's did), and blocking on "a batch exists" would lock out exactly the
  // people who need it. It warns, because double-paying is the one mistake here
  // that costs real money.
  const batchInFlight = batchStatus === 'submitting' || batchStatus === 'submitted';

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t('markPaid.title', { defaultValue: 'Record that this run was paid' })}
          </DialogTitle>
          <DialogDescription>
            {employeeCount === null
              ? t('markPaid.descriptionUnknownCount', {
                  defaultValue:
                    'Use this when employees were paid outside KaziPeople — a bank file uploaded at your bank, a transfer, or cash. It records who says so and how, and it makes payslips visible in My Pay to every employee paid by this run.',
                })
              : t('markPaid.description', {
                  defaultValue:
                    'Use this when employees were paid outside KaziPeople — a bank file uploaded at your bank, a transfer, or cash. It records who says so and how, and it makes payslips visible in My Pay to all {{count}} employees in this run.',
                  count: employeeCount,
                })}
          </DialogDescription>
        </DialogHeader>

        {batchInFlight && (
          <p className="flex items-start gap-2 rounded-lg border border-warning bg-warning-soft p-3 text-sm text-warning-strong">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>
              {t('markPaid.batchInFlight', {
                defaultValue:
                  'A KaziPeople payment for this run is already with the provider. Recording an outside payment does NOT stop it — check the Disbursements tab first, or these employees could be paid twice.',
              })}
            </span>
          </p>
        )}

        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground">
            {t('markPaid.methodLabel', { defaultValue: 'Where did the money actually go?' })}
          </p>
          {/* The rail, not the file. A tenant took the generic "Bank disbursement"
              review file to the M-Pesa bulk portal — the file said bank, the money
              went by mobile money — and picked the wrong answer because the
              question read as "which file did you download". */}
          <p className="text-xs text-muted-foreground">
            {t('markPaid.methodHelp', {
              defaultValue: 'The route the money travelled — not which file you downloaded. Name the file below.',
            })}
          </p>
          {RUN_EXTERNAL_PAYMENT_METHODS.map((m) => (
            <label
              key={m.code}
              className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-border p-3 text-sm"
            >
              <input
                type="radio"
                name="external-payment-method"
                className="mt-0.5"
                checked={method === m.code}
                onChange={() => setMethod(m.code)}
              />
              <span>
                <span className="font-medium text-foreground">
                  {t(`markPaid.method.${m.code}`, { defaultValue: m.label })}
                </span>
                {m.hint && (
                  <span className="block text-xs text-muted-foreground">
                    {t(`markPaid.methodHint.${m.code}`, { defaultValue: m.hint })}
                  </span>
                )}
              </span>
            </label>
          ))}
        </div>

        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">
            {t('markPaid.referenceLabel', { defaultValue: 'Reference' })}
          </p>
          <Input
            value={reference}
            maxLength={120}
            onChange={(e) => setReference(e.target.value)}
            placeholder={t('markPaid.referencePh', {
              defaultValue: 'e.g. the file you used, or the batch reference from the portal',
            })}
          />
          <p className="text-xs text-muted-foreground">
            {t('markPaid.referenceHint', {
              defaultValue:
                'Optional, but it is what lets anyone match this run to your bank statement later.',
            })}
          </p>
        </div>

        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">
            {t('markPaid.noteLabel', { defaultValue: 'Note' })}
          </p>
          <Textarea
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('markPaid.notePh', {
              defaultValue: 'e.g. uploaded at NMB on 26 Sep, value date 27 Sep',
            })}
          />
        </div>

        {needsExplanation && (
          <p className="text-xs text-danger">
            {t('markPaid.otherNeedsDetail', {
              defaultValue: '"Other" needs a reference or a short note saying how they were paid.',
            })}
          </p>
        )}

        <p className="text-xs text-muted-foreground">
          {t('markPaid.irreversible', {
            defaultValue:
              'This cannot be undone here — the run stays paid, and your name stays on the record.',
          })}
        </p>

        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={onClose}>
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            className="gap-2"
            disabled={busy || needsExplanation}
            onClick={() => onConfirm({ method, reference: reference.trim(), note: note.trim() })}
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('markPaid.confirm', { defaultValue: 'Record payment' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MarkPaidDialog;
